import { useState, useEffect, useCallback } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
} from 'firebase/auth';
import { auth } from '../services/firebase';

/**
 * Authentication hook wrapping Firebase onAuthStateChanged.
 * Provides reactive user state plus sign-in/sign-out actions.
 * Returns { user, loading, signIn, signOut, getIdToken }.
 */
export function useAuth() {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const signIn = useCallback(async (email, password) => {
    return signInWithEmailAndPassword(auth, email, password);
  }, []);

  const signOut = useCallback(async () => {
    localStorage.removeItem('offline_role');
    localStorage.removeItem('offline_pin');
    // Also clear any tutorial state just in case, though it's keyed by uid usually
    return firebaseSignOut(auth);
  }, []);

  /**
   * Get the current user's Firebase ID token (for API calls).
   * Returns null if not authenticated.
   */
  const getIdToken = useCallback(async () => {
    if (!auth.currentUser) return null;
    try {
      return await auth.currentUser.getIdToken(/* forceRefresh */ false);
    } catch {
      return null;
    }
  }, []);

  return { user, loading, signIn, signOut, getIdToken };
}
